import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Popconfirm, Select, Spin, Switch } from 'antd';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage } from '@api/server';
import { getAgents } from '@components/agents';
import {
  AGENT_TOOL_TYPE_LABELS,
  createAgentTool,
  deleteAgentTool,
  getAgentTool,
  updateAgentTool,
} from './agent-tools';

const AGENT_TOOL_TYPE_OPTIONS = Object.entries(AGENT_TOOL_TYPE_LABELS).map(([value, label]) => ({
  value: Number(value),
  label,
}));

const AgentTool = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { agentId, id, toolId } = useParams();
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const scopedAgentId = useMemo(() => {
    if (!agentId) {
      return null;
    }

    const nextAgentId = Number(agentId);
    return Number.isInteger(nextAgentId) && nextAgentId > 0 ? nextAgentId : null;
  }, [agentId]);
  const routeToolId = toolId || id;
  const agentToolId = useMemo(() => {
    if (!routeToolId) {
      return null;
    }

    const nextId = Number(routeToolId);
    return Number.isInteger(nextId) && nextId > 0 ? nextId : null;
  }, [routeToolId]);
  const isScoped = Boolean(agentId);
  const isNewAgentTool = !routeToolId;
  const title = isNewAgentTool ? 'Новый инструмент' : `Инструмент #${agentToolId || ''}`;
  const selectedVid = Form.useWatch('vid', form);
  const needsUrl = selectedVid === 2 || selectedVid === 4;
  const showExternalId = selectedVid !== 4;
  const urlExtra = selectedVid === 4
    ? 'Эндпоинт /api/rag справки (полный текст блоков).'
    : 'Для MCP укажите URL SSE-сервера.';
  const urlPlaceholder = selectedVid === 4
    ? 'https://help.webokna.online/api/rag'
    : 'https://api.example.com/sse';
  const urlRequiredMessage = selectedVid === 4
    ? 'Введите URL внешней RAG.'
    : 'Введите URL MCP-сервера.';
  const tokenExtra = selectedVid === 4
    ? 'Опционально: заголовок token, если эндпоинт RAG закрыт авторизацией.'
    : 'Если пусто, MCP использует SessionId из конфигурации компании.';
  const agentOptions = useMemo(() => agents.map((agent) => ({
    value: agent.id,
    label: `${agent.name} (${agent.code})`,
  })), [agents]);
  const listPath = useMemo(() => (
    isScoped ? '/agents' : '/agent-tools'
  ), [isScoped]);
  const closeDrawer = useCallback(() => {
    navigate(listPath);
  }, [listPath, navigate]);

  const loadAgentTool = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const agentsResult = await getAgents();
      const nextAgents = agentsResult || [];
      setAgents(nextAgents);

      if (isNewAgentTool) {
        form.setFieldsValue({
          name: '',
          agentid: scopedAgentId || nextAgents[0]?.id,
          vid: 2,
          url: '',
          externalid: '',
          token: '',
          active: true,
        });
        return;
      }

      if (!agentToolId) {
        setError('Некорректный идентификатор инструмента.');
        return;
      }

      const agentTool = await getAgentTool(agentToolId);
      form.setFieldsValue({
        name: agentTool.name,
        agentid: Number(agentTool.agentid),
        vid: Number(agentTool.vid),
        url: agentTool.url || '',
        externalid: agentTool.externalid || '',
        token: agentTool.token || '',
        active: Boolean(agentTool.active),
      });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [agentToolId, form, isNewAgentTool, scopedAgentId]);

  useEffect(() => {
    loadAgentTool();
  }, [loadAgentTool]);

  const handleSave = async (values) => {
    setError('');
    setIsSaving(true);
    try {
      const payload = {
        ...values,
        agentid: scopedAgentId || values.agentid,
        name: values.name?.trim(),
        url: values.url?.trim() || null,
        externalid: values.externalid?.trim() || null,
        token: values.token?.trim() || null,
      };

      if (isNewAgentTool) {
        await createAgentTool(payload);
        navigate(listPath, { state: { agentToolsRefreshKey: Date.now() } });
        return;
      }

      await updateAgentTool({
        id: agentToolId,
        ...payload,
      });
      navigate(listPath, { state: { agentToolsRefreshKey: Date.now() } });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!agentToolId) {
      return;
    }

    setError('');
    setIsDeleting(true);
    try {
      await deleteAgentTool(agentToolId);
      navigate(listPath, { state: { agentToolsRefreshKey: Date.now() } });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Drawer
      className="user-drawer agent-tool-drawer"
      title={title}
      placement="right"
      width={560}
      open
      onClose={closeDrawer}
      destroyOnHidden
      footer={(
        <div className="user-drawer-footer">
          <div className="user-drawer-footer-start">
            <Button type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={() => form.submit()}>
              Сохранить
            </Button>
            {!isNewAgentTool && (
              <Popconfirm
                title="Удалить инструмент?"
                description="Инструмент будет удален из агента."
                okText="Удалить"
                cancelText="Отмена"
                okButtonProps={{ danger: true, loading: isDeleting }}
                onConfirm={handleDelete}
              >
                <Button danger icon={<DeleteOutlined />} loading={isDeleting}>
                  Удалить
                </Button>
              </Popconfirm>
            )}
          </div>
          <Button onClick={closeDrawer}>Отмена</Button>
        </div>
      )}
    >
      {error && (
        <Alert className="user-alert" type="error" message={error} showIcon />
      )}

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          initialValues={{
            name: '',
            agentid: undefined,
            vid: 2,
            url: '',
            externalid: '',
            token: '',
            active: true,
          }}
        >
          <Form.Item
            label="Название"
            name="name"
            rules={[
              { required: true, message: 'Введите название инструмента.' },
              { max: 255, message: 'Название должно быть не длиннее 255 символов.' },
            ]}
          >
            <Input placeholder="Название инструмента" autoFocus />
          </Form.Item>

          <Form.Item
            label="Агент"
            name="agentid"
            rules={[{ required: true, message: 'Выберите агента.' }]}
          >
            <Select options={agentOptions} placeholder="Выберите агента" disabled={isScoped} />
          </Form.Item>

          <div className="form-row">
            <Form.Item
              label="Тип"
              name="vid"
              rules={[{ required: true, message: 'Выберите тип инструмента.' }]}
            >
              <Select options={AGENT_TOOL_TYPE_OPTIONS} />
            </Form.Item>

            <Form.Item label="Активен" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>

          <Form.Item
            label="URL"
            name="url"
            extra={urlExtra}
            rules={[
              { required: needsUrl, message: urlRequiredMessage },
              { max: 512, message: 'URL должен быть не длиннее 512 символов.' },
            ]}
          >
            <Input placeholder={urlPlaceholder} />
          </Form.Item>

          {showExternalId && (
            <Form.Item
              label="External ID"
              name="externalid"
              extra="Для file search: ID хранилища/индекса. Для MCP: server label. Для пространства: ID пространства."
              rules={[
                { required: selectedVid === 1, message: 'Введите External ID.' },
                { max: 512, message: 'External ID должен быть не длиннее 512 символов.' },
              ]}
            >
              <Input placeholder="external-id" />
            </Form.Item>
          )}

          <Form.Item
            label="Token"
            name="token"
            extra={tokenExtra}
            rules={[{ max: 80, message: 'Token должен быть не длиннее 80 символов.' }]}
          >
            <Input.Password placeholder="Token" />
          </Form.Item>

        </Form>
      </Spin>
    </Drawer>
  );
};

export default AgentTool;
